import { Timestamp } from 'firebase/firestore';
import type { AnexoPublicacao, CategoriaPublicacao, Publicacao, PublicacaoEditavel, PublicoCiencia } from '../publicacao.types';

/** Estado do formulário do editor (datas como 'AAAA-MM-DD' / 'AAAA-MM-DDTHH:mm' dos inputs). */
export interface FormPublicacao {
  titulo: string;
  resumo: string;
  categoria: CategoriaPublicacao | '';
  conteudo: string;
  capaUrl: string | null;
  capaPath: string | null;
  anexos: AnexoPublicacao[];
  /** Arquivos já salvos que a pessoa removeu (apaga no Storage ao salvar). */
  pathsRemovidos: string[];
  /** Enviados nesta edição (sobem na hora em que são escolhidos) — apagados se fechar sem salvar. */
  pathsEnviados: string[];
  exigeCiencia: boolean;
  publico: PublicoCiencia;
  projetoIds: string[];
  pessoaIds: string[];
  comPrazo: boolean;
  prazo: string;
  fixar: boolean;
  fixarComLimite: boolean;
  fixarAte: string;
  /** Só pra rascunho/agendada: publicar agora ou agendar. */
  agendar: boolean;
  /** Atalho pra um documento da aba Documentos (opcional). */
  documentoRelacionadoId: string;
  /** O documento escolhido pede ciência → o post não tem ciência própria (a do documento vale). */
  cienciaPeloDocumento: boolean;
  publicarEm: string;
}

export const FORM_VAZIO: FormPublicacao = {
  titulo: '',
  resumo: '',
  categoria: '',
  conteudo: '',
  capaUrl: null,
  capaPath: null,
  anexos: [],
  pathsRemovidos: [],
  pathsEnviados: [],
  exigeCiencia: false,
  publico: 'todos',
  projetoIds: [],
  pessoaIds: [],
  comPrazo: false,
  prazo: '',
  fixar: false,
  fixarComLimite: false,
  fixarAte: '',
  agendar: false,
  publicarEm: '',
  documentoRelacionadoId: '',
  cienciaPeloDocumento: false,
};

function paraInputData(valor: Timestamp | null | undefined): string {
  if (!valor) return '';
  const d = valor.toDate();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function paraInputDataHora(valor: Timestamp | null | undefined): string {
  if (!valor) return '';
  const d = valor.toDate();
  return `${paraInputData(valor)}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function deInputData(valor: string): Date | null {
  if (!valor) return null;
  const [a, m, d] = valor.split('-').map(Number);
  return new Date(a, m - 1, d);
}

export function formDe(publicacao: Publicacao): FormPublicacao {
  return {
    ...FORM_VAZIO,
    titulo: publicacao.titulo,
    resumo: publicacao.resumo,
    categoria: publicacao.categoria as CategoriaPublicacao,
    conteudo: publicacao.conteudo,
    capaUrl: publicacao.capaUrl,
    capaPath: publicacao.capaPath,
    anexos: publicacao.anexos ?? [],
    exigeCiencia: publicacao.ciencia?.exige ?? false,
    publico: publicacao.ciencia?.publico ?? 'todos',
    projetoIds: publicacao.ciencia?.projetoIds ?? [],
    pessoaIds: publicacao.ciencia?.publico === 'pessoas' ? (publicacao.ciencia.alvoIds ?? []) : [],
    comPrazo: Boolean(publicacao.ciencia?.prazo),
    prazo: paraInputData(publicacao.ciencia?.prazo),
    fixar: publicacao.fixada?.ativa ?? false,
    fixarComLimite: Boolean(publicacao.fixada?.ate),
    fixarAte: paraInputData(publicacao.fixada?.ate),
    agendar: publicacao.status === 'agendada',
    publicarEm: publicacao.status === 'agendada' ? paraInputDataHora(publicacao.publicarEm) : '',
    documentoRelacionadoId: publicacao.documentoRelacionadoId ?? '',
  };
}

/** O que falta preencher (mensagens pra tela); vazio = pode salvar. `publicando` = vai publicar/agendar. */
export function errosDoForm(form: FormPublicacao, publicando: boolean): string[] {
  const erros: string[] = [];
  if (!form.titulo.trim()) erros.push('Informe o título.');
  if (publicando) {
    if (!form.categoria) erros.push('Escolha a categoria.');
    if (!form.resumo.trim()) erros.push('Escreva o resumo do card.');
    if (!form.conteudo.trim()) erros.push('Escreva o texto.');
    if (form.agendar) {
      if (!form.publicarEm) erros.push('Informe quando publicar.');
      else if (new Date(form.publicarEm).getTime() <= Date.now()) erros.push('A data de agendamento precisa ser no futuro.');
    }
  }
  if (form.exigeCiencia && !form.cienciaPeloDocumento) {
    if (form.publico === 'projetos' && form.projetoIds.length === 0) erros.push('Escolha ao menos um projeto para a ciência.');
    if (form.publico === 'pessoas' && form.pessoaIds.length === 0) erros.push('Escolha ao menos uma pessoa para a ciência.');
    if (form.comPrazo && !form.prazo) erros.push('Informe o prazo da ciência.');
  }
  if (form.fixar && form.fixarComLimite && !form.fixarAte) erros.push('Informe até quando fica fixada.');
  return erros;
}

/** Monta o que vai pro Firestore. `alvoIds` já resolvido (pessoas escolhidas ou pelos projetos). */
export function editavelDe(
  form: FormPublicacao,
  arquivos: { capaUrl: string | null; capaPath: string | null; anexos: AnexoPublicacao[] },
  alvoIds: string[]
): PublicacaoEditavel {
  // Documento relacionado com ciência: a ciência é só dele (o post não cobra de novo).
  const exige = form.exigeCiencia && !form.cienciaPeloDocumento;
  const prazo = exige && form.comPrazo ? deInputData(form.prazo) : null;
  const fixarAte = form.fixar && form.fixarComLimite ? deInputData(form.fixarAte) : null;
  return {
    titulo: form.titulo.trim(),
    resumo: form.resumo.trim(),
    categoria: (form.categoria || 'comunicados') as CategoriaPublicacao,
    conteudo: form.conteudo,
    capaUrl: arquivos.capaUrl,
    capaPath: arquivos.capaPath,
    anexos: arquivos.anexos,
    ciencia: {
      exige,
      publico: form.publico,
      projetoIds: exige && form.publico === 'projetos' ? form.projetoIds : [],
      alvoIds: exige && form.publico !== 'todos' ? alvoIds : [],
      prazo: prazo ? Timestamp.fromDate(prazo) : null,
    },
    fixada: { ativa: form.fixar, ate: fixarAte ? Timestamp.fromDate(fixarAte) : null },
    documentoRelacionadoId: form.documentoRelacionadoId || null,
  };
}
